import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useUser } from './UserContext';

const ProjectContext = createContext();

export function useProject() {
  return useContext(ProjectContext);
}

function readStored(key) {
  try { return JSON.parse(localStorage.getItem(key)) ?? null; } catch { return null; }
}

function parseUser(user) {
  try { return JSON.parse(user) || {}; } catch { return {}; }
}

export function ProjectProvider({ children }) {
  const { user } = useUser();
  const userData = parseUser(user);

  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProject, _setSelectedProject] = useState(() => readStored('selectedProject'));
  const [selectedSite, _setSelectedSite] = useState(() => readStored('selectedSite'));

  // Load the project list.
  // Always fetch /api/access/me on load to get fresh role + projects from server,
  // so that role changes take effect without requiring re-login.
  useEffect(() => {
    if (!userData.userId) return;
    const tkn = localStorage.getItem('tkn');
    if (!tkn) return;

    fetch('/api/access/me', { headers: { Authorization: `Bearer ${tkn}` } })
      .then(r => r.json())
      .then(fresh => {
        if (!fresh.ok) return;
        const { role, projects } = fresh;

        if (!Array.isArray(projects)) {
          // admin / super_user — fetch all projects
          fetch('/api/access/projects')
            .then(r => r.json())
            .then(j => { if (j.ok) setAvailableProjects(j.projects || []); })
            .catch(() => {});
        } else if (projects.some(p => p.sites === null)) {
          // project_manager — fetch full list, filter to assigned
          const assignedIds = new Set(projects.map(p => p.id));
          fetch('/api/access/projects')
            .then(r => r.json())
            .then(j => {
              if (j.ok) setAvailableProjects((j.projects || []).filter(p => assignedIds.has(p.id)));
            })
            .catch(() => {});
        } else {
          // site_manager or no-role
          setAvailableProjects(projects);
        }
      })
      .catch(() => {
        // Fallback to cached login data if /me fails
        if (!Array.isArray(userData.projects)) {
          fetch('/api/access/projects')
            .then(r => r.json())
            .then(j => { if (j.ok) setAvailableProjects(j.projects || []); })
            .catch(() => {});
        } else {
          setAvailableProjects(userData.projects);
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.userId]);

  const setSelectedProject = useCallback((project) => {
    _setSelectedProject(project);
    _setSelectedSite(null);
    localStorage.setItem('selectedProject', JSON.stringify(project));
    localStorage.removeItem('selectedSite');
  }, []);

  const setSelectedSite = useCallback((site) => {
    _setSelectedSite(site);
    localStorage.setItem('selectedSite', JSON.stringify(site));
  }, []);

  // Sites available for the currently selected project
  const availableSites = (() => {
    if (!selectedProject) return [];
    const proj = availableProjects.find(p => p.id === selectedProject.id);
    return proj?.sites || [];
  })();

  return (
    <ProjectContext.Provider value={{
      availableProjects,
      selectedProject,
      setSelectedProject,
      availableSites,
      selectedSite,
      setSelectedSite,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}
